import { Params } from '@angular/router';

import { ViewId } from 'src/app/api/generated/view-id.generated';
import { NomObjectClass } from 'src/app/core/constants';
import { DataObjectRef } from 'src/app/core/models/object-ref.models';

/**
 * Query param a grid view honours on arrival: the uuid of the preset to open
 * with instead of the default (WAP-1638, the Admin -> View Presets deep link).
 * `DataGridComponent.estimateTabsFromViewPresets()` reads it once,
 * when the preset list first arrives; a uuid the list does not hold is ignored.
 */
export const VIEW_PRESET_QUERY_PARAM = 'viewPreset';

/**
 * Placeholders a `nom_view_preset_view.route_template` may carry. The template
 * is an absolute app path; `{context_object_id}` is the preset's bound object,
 * `{workspace_id}` its workspace (feeds, dashboards) and `{connector_id}` its
 * connector (rules). A template that needs a placeholder the row cannot fill
 * yields no link.
 */
const TEMPLATE_PLACEHOLDERS = {
  contextObjectId: '{context_object_id}',
  workspaceId: '{workspace_id}',
  connectorId: '{connector_id}',
} as const;

/**
 * Fallback route per view while the nomenclature's `route_template` is null
 * (seeded null in SEN-5223 - the frontend owns the routes, so this map is the
 * list handed over for seeding). Views that live inside another screen (a popup
 * grid, a panel) or need a context the row does not name have no entry: their
 * rows show the view name as plain text.
 */
export const VIEW_ROUTE_TEMPLATE_BY_VIEW_ID: Readonly<
  Partial<Record<ViewId, string>>
> = {
  [ViewId.Users]: '/admin/home',
  [ViewId.Roles]: '/admin/roles',
  [ViewId.UserGroups]: '/admin/groups',
  [ViewId.Companies]: '/admin/companies',
  [ViewId.Feeds]: '/analytics/feeds',
  [ViewId.Alerts]: '/analytics/alerts',
  [ViewId.ExportSchedules]: '/analytics/schedules',
  [ViewId.ActivityLog]: '/admin/activity-logs',
  [ViewId.SearchLog]: '/admin/search-logs',
  [ViewId.OutputLog]: '/admin/output-logs',
  [ViewId.SynopsisLog]: '/admin/synopsis-logs',
  [ViewId.DeveloperApiCompanyList]: '/admin/developer-api-company-list',
  [ViewId.DeveloperApiCompanyUsageList]:
    '/admin/developer-api-company-usage-list',
  [ViewId.PlatformIdentities]: '/admin/platform-identities',
  [ViewId.Categories]: '/admin/categories',
  [ViewId.Dashboards]: '/analytics/dashboards',
  [ViewId.Dimensions]: '/analytics/dimensions',
  [ViewId.HomeViewObjects]: '/analytics/home',
  [ViewId.WorkspaceViewObjects]: '/analytics/workspace/{context_object_id}',
  [ViewId.PanelViewWidgets]:
    '/analytics/workspace/{workspace_id}/feed/{context_object_id}',
  [ViewId.PanelLanguageWidgets]:
    '/analytics/workspace/{workspace_id}/feed/{context_object_id}',
  [ViewId.PaneViewFeedResults]:
    '/analytics/workspace/{workspace_id}/feed/{context_object_id}',
  [ViewId.PaneViewStoryPosts]:
    '/analytics/workspace/{workspace_id}/feed/{context_object_id}',
  [ViewId.ObjectViewDashboard]:
    '/analytics/workspace/{workspace_id}/dashboard/{context_object_id}',
  [ViewId.ObjectViewDashboardPage]:
    '/analytics/workspace/{workspace_id}/dashboard/{context_object_id}',
  [ViewId.ExportTemplates]: '/admin/export-templates',
  [ViewId.AutoStoryRuns]: '/analytics/auto-story-runs',
  [ViewId.ImportContentLogs]: '/source-catalog/import-content-logs',
  [ViewId.HttpRequestLog]: '/source-catalog/http-request-log',
  [ViewId.DataProviders]: '/source-catalog/data-providers',
  [ViewId.DataProviderHarvestLog]: '/source-catalog/data-provider-harvest-log',
  [ViewId.EngineSchedules]: '/admin/schedules',
  [ViewId.EngineScheduleRuns]: '/admin/schedules/{context_object_id}/runs',
  [ViewId.Sitemaps]: '/source-catalog/sitemaps',
  [ViewId.ObjectProperties]: '/sysadmin/object-property-management',
  [ViewId.HelpManager]: '/sysadmin/help-manager',
  [ViewId.NomenclatureManagement]: '/sysadmin/nomenclature-management',
  [ViewId.AdminExportSchedules]: '/admin/export-schedules',
  [ViewId.Connectors]: '/admin/connectors',
  [ViewId.Rules]: '/admin/rules',
  [ViewId.ConnectorRules]: '/analytics/connectors/{context_object_id}',
  [ViewId.ViewPresets]: '/admin/view-presets',
  [ViewId.OutputSettings]: '/admin/output-settings',
  [ViewId.AdminOperations]: '/admin/operations',
};

/** The row fields a deep link is resolved from. */
export interface ViewPresetDeepLinkSource {
  uuid?: string;
  view_id: string;
  context_object?: DataObjectRef | null;
}

/** A resolved deep link: `router.navigate(commands, { queryParams })`. */
export interface ViewPresetDeepLink {
  commands: string[];
  queryParams: Params;
}

/**
 * Resolve the screen a preset applies to. `routeTemplate` is the nomenclature's
 * `route_template` for the row's view (may be null); the enum-keyed map above is
 * the fallback. Returns null when neither names a route or a placeholder cannot
 * be filled from the row - the caller renders plain text then.
 */
export function resolveViewPresetDeepLink(
  row: ViewPresetDeepLinkSource,
  routeTemplate?: string | null,
): ViewPresetDeepLink | null {
  const template =
    routeTemplate?.trim() ||
    VIEW_ROUTE_TEMPLATE_BY_VIEW_ID[row.view_id as ViewId];
  if (!template) {
    return null;
  }

  const context = row.context_object ?? null;
  const values: Record<string, string | null | undefined> = {
    [TEMPLATE_PLACEHOLDERS.contextObjectId]: context?.id,
    [TEMPLATE_PLACEHOLDERS.workspaceId]:
      context?.object_class_id === NomObjectClass.WORKSPACE
        ? context.id
        : context?.workspace?.id,
    [TEMPLATE_PLACEHOLDERS.connectorId]: context?.connector?.id,
  };

  let path = template;
  for (const [placeholder, value] of Object.entries(values)) {
    if (!path.includes(placeholder)) {
      continue;
    }
    if (!value) {
      return null;
    }
    path = path.split(placeholder).join(value);
  }

  return {
    commands: [path],
    queryParams: row.uuid ? { [VIEW_PRESET_QUERY_PARAM]: row.uuid } : {},
  };
}
